import React from "react";
import {
  AbsoluteFill,
  useCurrentFrame,
  useVideoConfig,
  spring,
  interpolate,
  Easing,
  Audio,
  staticFile,
  Sequence,
  Img,
} from "remotion";
import {SPRINGS, FONT_SCALE, SAFE_ZONES} from "../presets/editing-styles";
import {CLAMP, FONT_STACK, IDV} from "../presets/idv";
import {AccentLine} from "../components/dimus/AccentLine";
import {KineticText} from "../components/text/KineticText";
import {SceneBackground} from "../components/backgrounds/SceneBackground";
import {Noise} from "../components/dimus/Noise";
import {Vignette} from "../components/dimus/Vignette";
import {FilmGrain} from "../components/dimus/FilmGrain";
import {OrganicLayer} from "../components/overlays/OrganicLayer";

/**
 * CAFÉ COM TEU ROI EP14 — "A Prisão do Dono"
 *
 * SPEC VFX — copy-to-vfx-adapter 7 etapas
 * Editing Style: DYNAMIC
 * Style: Provocador, direto,老板depois-joeira
 *
 * SFX Library: public/sfx/
 * BGM: cafe-lofi.mp3 @ 0.15
 * TTS: ElevenLabs @ 0.9
 */

// Durações reais (frames @ 30fps)
const SCENES = [
  {dur: 120,  file: "audio/cafe-ep14/cafe_ep14_c01.mp3", bg: "gradient-split" as const},   // 4s — HOOK
  {dur: 220,  file: "audio/cafe-ep14/cafe_ep14_c02.mp3", bg: "dark-void" as const},       // 7.3s — IMPRISONMENT
  {dur: 165,  file: "audio/cafe-ep14/cafe_ep14_c03.mp3", bg: "ratio-contrast" as const},  // 5.5s — RATIO
  {dur: 120,  file: "audio/cafe-ep14/cafe_ep14_c04.mp3", bg: "calendar-check" as const},  // 4s — PARADOX
  {dur: 120,  file: "audio/cafe-ep14/cafe_ep14_c05.mp3", bg: "liberation" as const},      // 4s — CTA
];

// Transition: 25 frames cada (≈0.83s)
const TRANSITION_DUR = 25;
export const CAFE_EP14_TOTAL = SCENES.reduce((s, sc) => s + sc.dur, 0) + SCENES.length * TRANSITION_DUR;

// Total: 120+220+165+120+120 + 125 = 870 frames (~29s)
const YELLOW = IDV.third;
const GREEN = IDV.secondary;
const BLUE = IDV.primary;
const RED = IDV.error;
const WHITE = "#f5f5f7";
const MUTED = "#a3a3a7";
const SZ = SAFE_ZONES.universal;

const BGM_VOLUME = 0.15;
const TTS_VOLUME = 0.9;

// ─── SceneFade com micro-animação ─────────────────────────────────
/**
 * Fade in 12f, fade out 15f.
 * Micro-animação: float sutil + noise overlay.
 */
const SceneFade: React.FC<{duration: number; children: React.ReactNode; microAnim?: "float" | "pulse" | "glow" | "shimmer"}> = ({
  duration,
  children,
  microAnim = "float",
}) => {
  const frame = useCurrentFrame();
  const fadeIn = interpolate(frame, [0, 12], [0, 1], CLAMP);
  const fadeOut = interpolate(frame, [duration - 15, duration], [1, 0], CLAMP);
  const opacity = Math.min(fadeIn, fadeOut);

  // Micro-animações
  const floatY = microAnim === "float" ? interpolate(frame, [0, 60], [0, 4], CLAMP) : 0;
  const pulseScale = microAnim === "pulse" ? interpolate(Math.sin(frame * 0.15), [-1, 1], [1, 1.03], CLAMP) : 1;
  const glowOpacity = microAnim === "glow" ? interpolate(Math.sin(frame * 0.1), [-1, 1], [0.3, 0.6], CLAMP) : 0;

  return (
    <OrganicLayer intensity="subtle" seed="cafe-ep14">
    <AbsoluteFill
      style={{
        opacity,
        transform: `translateY(${floatY}px) scale(${pulseScale})`,
      }}
    >
      {microAnim === "glow" && (
        <div
          style={{
            position: "absolute",
            inset: 0,
            boxShadow: `inset 0 0 ${60 + glowOpacity * 40}px ${glowOpacity * 30}px ${YELLOW}40`,
            pointerEvents: "none",
          }}
        />
      )}
      {children}
    </AbsoluteFill>
    </OrganicLayer>
  );
};

// ─── Pill badge ──────────────────────────────────────────────────────
const Pill: React.FC<{text: string; color: string; delay: number}> = ({text, color, delay}) => {
  const frame = useCurrentFrame();
  const {fps} = useVideoConfig();
  const p = spring({frame: frame - delay, fps, config: SPRINGS.bouncy});
  if (frame < delay) return null;
  return (
    <div
      style={{
        opacity: p,
        transform: `scale(${interpolate(p, [0, 1], [0.7, 1], CLAMP)})`,
        padding: "10px 20px",
        borderRadius: 999,
        background: `${color}18`,
        border: `1px solid ${color}44`,
      }}
    >
      <span style={{fontSize: FONT_SCALE.label.min, fontWeight: 700, color, fontFamily: FONT_STACK}}>{text}</span>
    </div>
  );
};

// ─── Gradient Split Background ───────────────────────────────────────
const GradientSplit: React.FC<{active: boolean}> = ({active}) => {
  const frame = useCurrentFrame();
  if (!active) return null;
  const split = interpolate(frame, [0, 30], [0, 1], CLAMP);
  return (
    <AbsoluteFill style={{overflow: "hidden"}}>
      <div
        style={{
          position: "absolute",
          left: 0,
          top: 0,
          width: `${split * 100}%`,
          height: "100%",
          background: `linear-gradient(135deg, ${IDV.primary}15, #000)`,
        }}
      />
      <div
        style={{
          position: "absolute",
          right: 0,
          top: 0,
          width: `${(1 - split) * 100}%`,
          height: "100%",
          background: `linear-gradient(225deg, ${YELLOW}10, #000)`,
        }}
      />
    </AbsoluteFill>
  );
};

// ─── Ratio Arrow Element ─────────────────────────────────────────────
const RatioArrows: React.FC<{active: boolean}> = ({active}) => {
  const frame = useCurrentFrame();
  if (!active) return null;
  const offset1 = interpolate(frame, [0, 40], [-50, 0], CLAMP);
  const offset2 = interpolate(frame, [20, 60], [50, 0], CLAMP);
  return (
    <div style={{position: "absolute", right: 80, top: "30%", display: "flex", flexDirection: "column", gap: 20}}>
      <div
        style={{
          opacity: frame > 10 ? 1 : 0,
          transform: `translateX(${offset1}px)`,
          fontSize: 72,
          fontWeight: 900,
          color: RED,
          fontFamily: FONT_STACK,
          textShadow: `0 0 30px ${RED}60`,
        }}
      >
        PRA
      </div>
      <div
        style={{
          opacity: frame > 30 ? 1 : 0,
          transform: `translateX(${offset2}px)`,
          fontSize: 72,
          fontWeight: 900,
          color: GREEN,
          fontFamily: FONT_STACK,
          textShadow: `0 0 30px ${GREEN}60`,
        }}
      >
        NO
      </div>
    </div>
  );
};

// ─── Calendar Check Element ───────────────────────────────────────────
const CalendarCheck: React.FC<{active: boolean}> = ({active}) => {
  const frame = useCurrentFrame();
  if (!active) return null;
  const scale = spring({frame: frame - 10, fps: 30, config: SPRINGS.bouncy});
  const checkDraw = interpolate(frame, [15, 35], [0, 1], CLAMP);
  return (
    <div
      style={{
        position: "absolute",
        left: 80,
        top: "25%",
        opacity: frame > 5 ? 1 : 0,
        transform: `scale(${scale})`,
      }}
    >
      <div
        style={{
          width: 160,
          height: 160,
          borderRadius: 20,
          background: `${GREEN}20`,
          border: `3px solid ${GREEN}`,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          boxShadow: `0 0 40px ${GREEN}30`,
        }}
      >
        <svg width="80" height="80" viewBox="0 0 80 80">
          <polyline
            points="15,42 33,60 65,20"
            fill="none"
            stroke={GREEN}
            strokeWidth="6"
            strokeLinecap="round"
            strokeLinejoin="round"
            style={{
              strokeDasharray: 80,
              strokeDashoffset: interpolate(checkDraw, [0, 1], [80, 0], CLAMP),
            }}
          />
        </svg>
      </div>
    </div>
  );
};

// ─── Prison Cell Element ─────────────────────────────────────────────
const PrisonCell: React.FC<{active: boolean}> = ({active}) => {
  const frame = useCurrentFrame();
  if (!active) return null;
  const scale = spring({frame: frame - 5, fps: 30, config: SPRINGS.heavy});
  const barsOpacity = interpolate(frame, [10, 30], [0, 0.6], CLAMP);
  return (
    <div
      style={{
        position: "absolute",
        right: 60,
        top: "20%",
        opacity: frame > 3 ? 1 : 0,
        transform: `scale(${scale})`,
      }}
    >
      <div
        style={{
          width: 200,
          height: 280,
          borderRadius: 16,
          background: `${BLUE}15`,
          border: `3px solid ${BLUE}60`,
          boxShadow: `0 0 60px ${BLUE}20`,
          overflow: "hidden",
          position: "relative",
        }}
      >
        {/* Bars */}
        {[0, 1, 2, 3].map((i) => (
          <div
            key={i}
            style={{
              position: "absolute",
              left: `${20 + i * 45}px`,
              top: 0,
              width: 8,
              height: "100%",
              background: `${BLUE}${Math.round(barsOpacity * 255).toString(16).padStart(2, "0")}`,
              borderRadius: 4,
            }}
          />
        ))}
        {/* Person silhouette */}
        <div
          style={{
            position: "absolute",
            bottom: 20,
            left: "50%",
            transform: "translateX(-50%)",
            width: 60,
            height: 60,
            borderRadius: "50%",
            background: `${MUTED}40`,
            border: `2px solid ${MUTED}`,
          }}
        />
      </div>
    </div>
  );
};

// ─── SCENE 1: HOOK — REVERSAL ───────────────────────────────────────
const S1: React.FC = () => {
  const frame = useCurrentFrame();
  const {fps} = useVideoConfig();
  const titleP = spring({frame: frame - 8, fps, config: SPRINGS.bouncy});
  const lineP = spring({frame: frame - 25, fps, config: SPRINGS.snappy});
  const splitP = spring({frame: frame - 3, fps, config: SPRINGS.smooth});

  return (
    <AbsoluteFill>
      <SceneBackground mode="gradient-drift" />
      <GradientSplit active={frame < 30} />
      <AbsoluteFill style={{justifyContent: "center", alignItems: "center", padding: `${SZ.top}px ${SZ.left}px ${SZ.bottom}px`, flexDirection: "column", gap: 16}}>
        <Audio src={staticFile(SCENES[0].file)} volume={TTS_VOLUME} />
        <Pill text="EP14" color={YELLOW} delay={5} />

        {/* Main hook text */}
        <div
          style={{
            opacity: titleP,
            transform: `translateY(${interpolate(titleP, [0, 1], [30, 0], CLAMP)}px)`,
            fontSize: FONT_SCALE.h1.min,
            fontWeight: 800,
            color: WHITE,
            fontFamily: FONT_STACK,
            letterSpacing: -1,
            textAlign: "center",
            maxWidth: "85%",
            lineHeight: 1.1,
          }}
        >
          Você acha que tem{" "}
          <span style={{color: YELLOW}}>um negócio.</span>
        </div>

        <div
          style={{
            opacity: titleP,
            transform: `translateY(${interpolate(titleP, [0, 1], [30, 0], CLAMP)}px)`,
            fontSize: FONT_SCALE.h1.min,
            fontWeight: 800,
            color: WHITE,
            fontFamily: FONT_STACK,
            letterSpacing: -1,
            textAlign: "center",
            maxWidth: "85%",
            lineHeight: 1.1,
          }}
        >
          Mas quando você{" "}
          <span style={{color: RED}}>PARA,</span>
        </div>

        <div
          style={{
            opacity: titleP,
            transform: `translateY(${interpolate(titleP, [0, 1], [30, 0], CLAMP)}px)`,
            fontSize: FONT_SCALE.h1.min,
            fontWeight: 800,
            color: WHITE,
            fontFamily: FONT_STACK,
            letterSpacing: -1,
            textAlign: "center",
            maxWidth: "85%",
            lineHeight: 1.1,
          }}
        >
          o caixa também{" "}
          <span style={{color: RED, textShadow: `0 0 40px ${RED}80`}}>PARA.</span>
        </div>

        {/* Accent line */}
        <div
          style={{
            opacity: lineP,
            transform: `scaleX(${lineP})`,
            transformOrigin: "center",
            width: 300,
            height: 4,
            background: `linear-gradient(90deg, ${YELLOW}, ${RED})`,
            borderRadius: 2,
            marginTop: 8,
            boxShadow: `0 0 20px ${YELLOW}40`,
          }}
        />
      </AbsoluteFill>
    </AbsoluteFill>
  );
};

// ─── SCENE 2: IMPRISONMENT ────────────────────────────────────────────
const S2: React.FC = () => {
  const frame = useCurrentFrame();
  const {fps} = useVideoConfig();
  const p1 = spring({frame: frame - 10, fps, config: SPRINGS.snappy});
  const p2 = spring({frame: frame - 50, fps, config: SPRINGS.snappy});
  const p3 = spring({frame: frame - 90, fps, config: SPRINGS.snappy});
  const p4 = spring({frame: frame - 130, fps, config: SPRINGS.bouncy});

  const lines = [
    "Tudo começa com orgulho.",
    "Você montou a empresa, botou teu nome.",
    "Fatura 50, 80, 100 por mês.",
    "Fala pros amigos: 'tenho minha empresa.'",
  ];

  return (
    <AbsoluteFill>
      <SceneBackground mode="dark-void" />
      <PrisonCell active={frame > 5} />
      <AbsoluteFill
        style={{
          justifyContent: "center",
          alignItems: "flex-start",
          padding: `${SZ.top + 100}px ${SZ.left + 60}px ${SZ.bottom}px`,
          flexDirection: "column",
          gap: 24,
        }}
      >
        <Audio src={staticFile(SCENES[1].file)} volume={TTS_VOLUME} />
        <Pill text="O DIAGNÓSTICO" color={BLUE} delay={5} />

        {lines.map((line, i) => {
          const delay = [10, 50, 90, 130][i];
          const opacity = spring({frame: frame - delay, fps, config: SPRINGS.snappy});
          const translateX = interpolate(opacity, [0, 1], [-30, 0], CLAMP);
          return (
            <div
              key={i}
              style={{
                opacity,
                transform: `translateX(${translateX}px)`,
              }}
            >
              <span
                style={{
                  fontSize: FONT_SCALE.h2.min,
                  fontWeight: 700,
                  color: i === 3 ? RED : WHITE,
                  fontFamily: FONT_STACK,
                  textShadow: i === 3 ? `0 0 30px ${RED}60` : "none",
                }}
              >
                {line}
              </span>
            </div>
          );
        })}

        {/* Impact question */}
        <div
          style={{
            opacity: p4,
            transform: `translateY(${interpolate(p4, [0, 1], [20, 0], CLAMP)}px)`,
            marginTop: 16,
          }}
        >
          <span
            style={{
              fontSize: FONT_SCALE.h2.min,
              fontWeight: 800,
              color: YELLOW,
              fontFamily: FONT_STACK,
              textShadow: `0 0 40px ${YELLOW}60`,
            }}
          >
            Mas se você SUMIR por 30 dias?
          </span>
        </div>
      </AbsoluteFill>
    </AbsoluteFill>
  );
};

// ─── SCENE 3: RATIO ──────────────────────────────────────────────────
const S3: React.FC = () => {
  const frame = useCurrentFrame();
  const {fps} = useVideoConfig();
  const textP = spring({frame: frame - 10, fps, config: SPRINGS.snappy});
  const praP = spring({frame: frame - 40, fps, config: SPRINGS.snappy});
  const noP = spring({frame: frame - 60, fps, config: SPRINGS.snappy});

  return (
    <AbsoluteFill>
      <SceneBackground mode="grid-dots" />
      <RatioArrows active={frame > 30} />
      <AbsoluteFill
        style={{
          justifyContent: "center",
          alignItems: "flex-end",
          padding: `${SZ.top + 80}px ${SZ.left + 60}px ${SZ.bottom}px`,
          flexDirection: "column",
          gap: 16,
        }}
      >
        <Audio src={staticFile(SCENES[2].file)} volume={TTS_VOLUME} />
        <Pill text="O PARADOXO" color={GREEN} delay={5} />

        <div
          style={{
            opacity: textP,
            transform: `translateX(${interpolate(textP, [0, 1], [20, 0], CLAMP)}px)`,
            fontSize: FONT_SCALE.body.max,
            fontWeight: 500,
            color: MUTED,
            fontFamily: FONT_STACK,
            textAlign: "right",
            maxWidth: "50%",
          }}
        >
          A maioria dos donos no Brasil são assim.
        </div>

        <div
          style={{
            opacity: textP,
            fontSize: FONT_SCALE.h2.min,
            fontWeight: 800,
            color: WHITE,
            fontFamily: FONT_STACK,
            textAlign: "right",
            maxWidth: "55%",
            lineHeight: 1.2,
          }}
        >
          Trabalham{" "}
          <span style={{color: RED, textShadow: `0 0 30px ${RED}60`}}>
            PRA
          </span>{" "}
          o negócio.
        </div>

        <div
          style={{
            opacity: textP,
            fontSize: FONT_SCALE.h2.min,
            fontWeight: 800,
            color: WHITE,
            fontFamily: FONT_STACK,
            textAlign: "right",
            maxWidth: "55%",
            lineHeight: 1.2,
          }}
        >
          Não trabalham{" "}
          <span style={{color: GREEN, textShadow: `0 0 30px ${GREEN}60`}}>
            NO
          </span>{" "}
          negócio.
        </div>

        <div
          style={{
            opacity: praP,
            transform: `translateY(${interpolate(praP, [0, 1], [15, 0], CLAMP)}px)`,
            fontSize: FONT_SCALE.body.max,
            fontWeight: 600,
            color: YELLOW,
            fontFamily: FONT_STACK,
            textAlign: "right",
            marginTop: 8,
          }}
        >
          O faturamento cresce. O trabalho também.
        </div>
      </AbsoluteFill>
    </AbsoluteFill>
  );
};

// ─── SCENE 4: PARADOX PROOF ─────────────────────────────────────────
const S4: React.FC = () => {
  const frame = useCurrentFrame();
  const {fps} = useVideoConfig();
  const p1 = spring({frame: frame - 10, fps, config: SPRINGS.snappy});
  const p2 = spring({frame: frame - 60, fps, config: SPRINGS.bouncy});

  return (
    <AbsoluteFill>
      <SceneBackground mode="floating-shapes" />
      <CalendarCheck active={frame > 5} />
      <AbsoluteFill
        style={{
          justifyContent: "center",
          alignItems: "center",
          padding: `${SZ.top + 60}px ${SZ.left}px ${SZ.bottom}px`,
          flexDirection: "column",
          gap: 20,
        }}
      >
        <Audio src={staticFile(SCENES[3].file)} volume={TTS_VOLUME} />
        <Pill text="A PROVA" color={YELLOW} delay={5} />

        <div
          style={{
            opacity: p1,
            transform: `translateY(${interpolate(p1, [0, 1], [20, 0], CLAMP)}px)`,
            fontSize: FONT_SCALE.h2.min,
            fontWeight: 700,
            color: WHITE,
            fontFamily: FONT_STACK,
            textAlign: "center",
            maxWidth: "80%",
            lineHeight: 1.2,
          }}
        >
          Já viu aquele dono que tira férias...
        </div>

        <div
          style={{
            opacity: p1,
            fontSize: FONT_SCALE.h2.min,
            fontWeight: 700,
            color: WHITE,
            fontFamily: FONT_STACK,
            textAlign: "center",
            maxWidth: "80%",
            lineHeight: 1.2,
          }}
        >
          E quando volta, o negócio tá
        </div>

        <div
          style={{
            opacity: p2,
            transform: `translateY(${interpolate(p2, [0, 1], [15, 0], CLAMP)}px)`,
            fontSize: FONT_SCALE.hero.min,
            fontWeight: 900,
            color: GREEN,
            fontFamily: FONT_STACK,
            textShadow: `0 0 60px ${GREEN}80`,
            letterSpacing: -2,
          }}
        >
          MAIOR?
        </div>

        <div
          style={{
            opacity: p2,
            fontSize: FONT_SCALE.body.max,
            fontWeight: 500,
            color: MUTED,
            fontFamily: FONT_STACK,
            fontStyle: "italic",
            marginTop: 12,
          }}
        >
          Não é porque ele tem processo.
        </div>
      </AbsoluteFill>
    </AbsoluteFill>
  );
};

// ─── SCENE 5: CTA — LIBERATION ──────────────────────────────────────
const S5: React.FC = () => {
  const frame = useCurrentFrame();
  const {fps} = useVideoConfig();
  const ctaP = spring({frame: frame - 8, fps, config: SPRINGS.bouncy});
  const profileP = spring({frame: frame - 60, fps, config: SPRINGS.snappy});
  const btnP = spring({frame: frame - 100, fps, config: SPRINGS.bouncy});

  return (
    <AbsoluteFill>
      <SceneBackground mode="warm-glow" />
      <AbsoluteFill
        style={{
          justifyContent: "center",
          alignItems: "center",
          padding: `${SZ.top}px ${SZ.left}px ${SZ.bottom}px`,
          flexDirection: "column",
          gap: 20,
        }}
      >
        <Audio src={staticFile(SCENES[4].file)} volume={TTS_VOLUME} />
        <Pill text="O PRÓXIMO PASSO" color={GREEN} delay={5} />

        <div
          style={{
            opacity: ctaP,
            transform: `translateY(${interpolate(ctaP, [0, 1], [25, 0], CLAMP)}px)`,
            fontSize: FONT_SCALE.h2.min,
            fontWeight: 700,
            color: WHITE,
            fontFamily: FONT_STACK,
            textAlign: "center",
            maxWidth: "85%",
            lineHeight: 1.2,
          }}
        >
          O primeiro passo pra sair dessa prisão?
        </div>

        <div
          style={{
            opacity: ctaP,
            fontSize: FONT_SCALE.h1.min,
            fontWeight: 800,
            color: YELLOW,
            fontFamily: FONT_STACK,
            textShadow: `0 0 50px ${YELLOW}60`,
            letterSpacing: -1,
            textAlign: "center",
          }}
        >
          PEGA UMA COISA.
        </div>

        <div
          style={{
            opacity: ctaP,
            fontSize: FONT_SCALE.body.max,
            fontWeight: 500,
            color: MUTED,
            fontFamily: FONT_STACK,
            textAlign: "center",
            maxWidth: "75%",
          }}
        >
          Que você faz todo dia.
        </div>

        <div
          style={{
            opacity: ctaP,
            fontSize: FONT_SCALE.body.max,
            fontWeight: 500,
            color: MUTED,
            fontFamily: FONT_STACK,
            textAlign: "center",
            maxWidth: "75%",
          }}
        >
          E documenta como ela funciona.
        </div>

        <div
          style={{
            opacity: btnP,
            transform: `scale(${interpolate(btnP, [0, 1], [0.8, 1], CLAMP)})`,
            padding: "16px 40px",
            borderRadius: 999,
            background: `${GREEN}20`,
            border: `2px solid ${GREEN}`,
            boxShadow: `0 0 40px ${GREEN}30`,
            marginTop: 16,
          }}
        >
          <span
            style={{
              fontSize: FONT_SCALE.cta.min,
              fontWeight: 700,
              color: GREEN,
              fontFamily: FONT_STACK,
              letterSpacing: 1,
            }}
          >
            HOJE
          </span>
        </div>

        {/* Profile Instagram CTA */}
        <div
          style={{
            opacity: profileP,
            transform: `translateY(${interpolate(profileP, [0, 1], [15, 0], CLAMP)}px)`,
            display: "flex",
            alignItems: "center",
            gap: 14,
            marginTop: 24,
          }}
        >
          <div
            style={{
              width: 52,
              height: 52,
              borderRadius: "50%",
              overflow: "hidden",
              border: `3px solid ${GREEN}`,
              boxShadow: `0 0 20px ${GREEN}30`,
            }}
          >
            <Img
              src={staticFile("assets/profiles/guilhermeribeiro-ai.jpg")}
              style={{width: "100%", height: "100%", objectFit: "cover"}}
            />
          </div>
          <div>
            <div style={{fontSize: FONT_SCALE.body.min, fontWeight: 700, color: WHITE, fontFamily: FONT_STACK}}>
              @guilhermeribeiro.ai
            </div>
            <div style={{fontSize: FONT_SCALE.label.min, fontWeight: 400, color: MUTED, fontFamily: FONT_STACK}}>
              Café com teu ROI
            </div>
          </div>
        </div>

        <div
          style={{
            opacity: profileP,
            fontSize: FONT_SCALE.body.min,
            fontWeight: 500,
            color: MUTED,
            fontFamily: FONT_STACK,
            fontStyle: "italic",
            marginTop: 8,
          }}
        >
          Até amanhã.
        </div>
      </AbsoluteFill>
    </AbsoluteFill>
  );
};

// ─── MAIN ─────────────────────────────────────────────────────────────
export const CafeEp14: React.FC = () => {
  // Build timeline with transitions between scenes
  const timeline: Array<{from: number; dur: number}> = [];
  let offset = 0;
  for (let i = 0; i < SCENES.length; i++) {
    timeline.push({from: offset, dur: SCENES[i].dur});
    offset += SCENES[i].dur;
    if (i < SCENES.length - 1) {
      offset += TRANSITION_DUR;
    }
  }

  const microAnims: Array<"float" | "pulse" | "glow" | "shimmer"> = ["glow", "pulse", "shimmer", "float", "glow"];

  return (
    <AbsoluteFill style={{backgroundColor: "#000"}}>
      {/* Background ambient layers */}
      <div
        style={{
          position: "absolute",
          inset: 0,
          background: `radial-gradient(ellipse at 50% 30%, ${BLUE}18, #000 70%)`,
        }}
      />
      <Noise opacity={0.02} />
      <FilmGrain opacity={0.025} />
      <Vignette intensity={0.5} />

      {/* BGM with fade in/out */}
      <Audio
        src={staticFile("music/cafe-lofi.mp3")}
        volume={(frame) => {
          const total = CAFE_EP14_TOTAL;
          const fadeIn = interpolate(frame, [0, 45], [0, BGM_VOLUME], {extrapolateRight: "clamp"});
          const fadeOut = interpolate(frame, [total - 60, total], [BGM_VOLUME, 0], {extrapolateLeft: "clamp", extrapolateRight: "clamp"});
          return Math.min(fadeIn, fadeOut);
        }}
        loop
      />

      {/* Scenes with SceneFade transitions */}
      {SCENES.map(({dur, bg}, i) => {
        const sequenceStart = timeline[i].from;
        return (
          <Sequence key={i} from={sequenceStart} durationInFrames={dur}>
            <SceneFade duration={dur} microAnim={microAnims[i]}>
              {[S1, S2, S3, S4, S5][i]()}
            </SceneFade>
          </Sequence>
        );
      })}
    </AbsoluteFill>
  );
};

CafeEp14.displayName = "CafeEp14";

;
    var _remotion_globalVariableA;
    var _remotion_globalVariableB;
    // Legacy CSS implementations will `eval` browser code in a Node.js context
    // to extract CSS. For backwards compatibility, we need to check we're in a
    // browser context before continuing.
    if (typeof self !== 'undefined' &&
        // AMP / No-JS mode does not inject these helpers:
        '$RefreshHelpers$' in self) {
        const currentExports = __webpack_module__.exports;
        const prevExports = (_remotion_globalVariableB = (_remotion_globalVariableA = __webpack_module__.hot.data) === null || _remotion_globalVariableA === void 0 ? void 0 : _remotion_globalVariableA.prevExports) !== null && _remotion_globalVariableB !== void 0 ? _remotion_globalVariableB : null;
        // This cannot happen in MainTemplate because the exports mismatch between
        // templating and execution.
        self.$RefreshHelpers$.registerExportsForReactRefresh(currentExports, __webpack_module__.id);
        // A module can be accepted automatically based on its exports, e.g. when
        // it is a Refresh Boundary.
        if (self.$RefreshHelpers$.isReactRefreshBoundary(currentExports)) {
            // Save the previous exports on update so we can compare the boundary
            // signatures.
            __webpack_module__.hot.dispose((data) => {
                data.prevExports = currentExports;
            });
            // Unconditionally accept an update to this module, we'll check if it's
            // still a Refresh Boundary later.
            __webpack_module__.hot.accept();
            // This field is set when the previous version of this module was a
            // Refresh Boundary, letting us know we need to check for invalidation or
            // enqueue an update.
            if (prevExports !== null) {
                // A boundary can become ineligible if its exports are incompatible
                // with the previous exports.
                //
                // For example, if you add/remove/change exports, we'll want to
                // re-execute the importing modules, and force those components to
                // re-render. Similarly, if you convert a class component to a
                // function, we want to invalidate the boundary.
                if (self.$RefreshHelpers$.shouldInvalidateReactRefreshBoundary(prevExports, currentExports)) {
                    __webpack_module__.hot.invalidate();
                }
                else {
                    self.$RefreshHelpers$.scheduleUpdate();
                }
            }
        }
        else {
            // Since we just executed the code for the module, it's possible that the
            // new exports made it ineligible for being a boundary.
            // We only care about the case when we were _previously_ a boundary,
            // because we already accepted this update (accidental side effect).
            const isNoLongerABoundary = prevExports !== null;
            if (isNoLongerABoundary) {
                __webpack_module__.hot.invalidate();
            }
        }
    }
